import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/hooks/useSmoothScroll";

/* ---------- IntersectionObserver hook ---------- */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        }),
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ---------- Per-word masked reveal ---------- */
export function MaskWords({ text, className = "", stagger = 0.05, start = 0, Tag = "span" }) {
  const [ref, inView] = useInView(0.2);
  const words = String(text).split(" ");
  return (
    <Tag ref={ref} className={className} aria-label={text}>
      {words.map((w, i) => (
        <span key={i} className={`word ${inView ? "in" : ""}`} style={{ marginRight: "0.24em" }}>
          <span style={{ "--d": `${start + i * stagger}s` }}>{w}</span>
        </span>
      ))}
    </Tag>
  );
}

/* ---------- Section header: hairline + label + index ---------- */
export function SectionHead({ label, index, dark = false }) {
  const [ref, inView] = useInView(0.4);
  const c = dark ? "text-white/60" : "text-black/55";
  return (
    <div ref={ref}>
      <div className={`${dark ? "bg-white/25" : "bg-black/20"} rule ${inView ? "in" : ""}`} />
      <div className="flex items-center justify-between pt-4">
        <span className={`eyebrow ${c}`}>{label}</span>
        <span className={`eyebrow tnum ${c}`}>{index}</span>
      </div>
    </div>
  );
}

/* ---------- Magnetic wrapper ---------- */
export function Magnetic({ children, strength = 0.4, className = "" }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - (r.left + r.width / 2);
    const y = e.clientY - (r.top + r.height / 2);
    el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "translate(0px, 0px)";
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={"transition-transform duration-500 ease-out will-change-transform " + className}
    >
      {children}
    </div>
  );
}

/* ---------- Pill (no classic buttons) ---------- */
export function Pill({ children, href, onClick, dark = false, target, className = "" }) {
  const theme = dark
    ? "border-white/30 text-white hover:bg-white hover:text-black"
    : "border-black/25 text-black hover:bg-black hover:text-paper";
  const base = `group inline-flex items-center gap-3 rounded-full border px-7 py-4 eyebrow transition-colors duration-500 ${theme} ${className}`;
  const inner = (
    <>
      <span>{children}</span>
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70 transition-transform duration-500 group-hover:scale-150" />
    </>
  );
  if (href)
    return (
      <Magnetic strength={0.3} className="inline-block">
        <a data-cursor="link" href={href} onClick={onClick} target={target} rel={target ? "noreferrer" : undefined} className={base}>
          {inner}
        </a>
      </Magnetic>
    );
  return (
    <Magnetic strength={0.3} className="inline-block">
      <button data-cursor="link" onClick={onClick} className={base}>
        {inner}
      </button>
    </Magnetic>
  );
}

/* ---------- Config-driven button (bilingual label + internal/external href) ---------- */
export function SectionButton({ btn, dark = false }) {
  const { lang } = useLang();
  const navigate = useNavigate();
  if (!btn || btn.visible === false) return null;
  const label = btn.label?.[lang] || btn.label?.ro || btn.label?.en || "";
  if (!label) return null;
  const href = btn.href || "#";
  const external = /^https?:\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
  if (external) {
    return (
      <Pill dark={dark} href={href} target={/^https?:\/\//.test(href) ? "_blank" : undefined}>
        {label}
      </Pill>
    );
  }
  if (href.startsWith("/")) {
    return (
      <Pill dark={dark} onClick={() => navigate(href)}>
        {label}
      </Pill>
    );
  }
  return (
    <Pill
      dark={dark}
      onClick={() => scrollToId(href.startsWith("#") ? href : "#" + href)}
    >
      {label}
    </Pill>
  );
}

export function SectionButtons({ buttons = [], dark = false, className = "" }) {
  const visible = (buttons || []).filter((b) => b && b.visible !== false);
  if (!visible.length) return null;
  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {visible.map((b, i) => (
        <SectionButton key={b.id || i} btn={b} dark={dark} />
      ))}
    </div>
  );
}
export function VLabel({ children, dark = false }) {
  return (
    <div className="flex flex-col items-center gap-4 h-full">
      <span className={`w-px flex-1 ${dark ? "bg-white/25" : "bg-black/20"}`} />
      <span className={`[writing-mode:vertical-rl] rotate-180 eyebrow ${dark ? "text-white/60" : "text-black/55"}`}>
        {children}
      </span>
    </div>
  );
}
